import { create, type StoreApi, type UseBoundStore } from "zustand";

import type {
  AnyChatServerFrame,
  ChatDockItem,
  ChatError,
  ChatMessage,
  SystemNotice,
} from "./types";

// Generic extraction of the consumer advisor Zustand store (plan 188 W2-M8,
// task 801). The reducer logic (`handleServerFrame`) is verbatim from
// `frontend/consumer/src/stores/advisor.ts` — only the render-payload type
// is now a type parameter (`TRenderPayload`) instead of hard-wired to the
// consumer's generated `RenderPayload` union, so the Pro CRM Copilot dock
// (M8b, which has no render payloads yet) can instantiate the same store
// shape. The consumer store stays the source of truth for its own concrete
// types (`AdvisorMessage`, `RenderPayload`) via a thin wrapper — see
// `frontend/consumer/src/stores/advisor.ts`.
//
// Frame handling reads `frame.type` plus the known per-type fields via a
// narrow cast; this trades the original file's compile-time exhaustiveness
// check (a `never`-typed switch default) for genericity. Each app's concrete
// `ServerFrame` union still gets full type-checking at the call site where
// `onServerFrame`/`handleServerFrame` is invoked with a concrete frame.

export type ChatStore<TRenderPayload> = {
  messages: ChatMessage<TRenderPayload>[];
  isStreaming: boolean;
  conversationId: string | null;
  activeToolCallIds: string[];
  // Name of the most recently started, still-running tool (e.g. "list_clients")
  // — drives a human "working" label in the UI. "" when no tool is running.
  currentToolName: string;
  dockItems: ChatDockItem<TRenderPayload>[];
  lastError: ChatError | null;
  hasUnreadAdvisorMessage: boolean;

  systemNotices: SystemNotice[];
  pendingFeedbackPrompt: string | null;
  needsAuthRefresh: boolean;
  profileSnapshotRefreshNonce: number;
  latestProfileSnapshotId: string | null;
  latestProfileRecencyToken: string | null;

  addMessage: (message: ChatMessage<TRenderPayload>) => void;
  appendToLast: (chunk: string) => void;
  setStreaming: (streaming: boolean) => void;
  stopActiveTurn: () => void;
  setConversationId: (id: string) => void;
  markAdvisorMessagesRead: () => void;
  clearMessages: () => void;
  dismissSystemNotice: (id: string) => void;
  clearPendingFeedbackPrompt: () => void;
  clearAuthRefresh: () => void;
  handleServerFrame: (
    frame: AnyChatServerFrame & Record<string, unknown>,
  ) => void;
  updateDockItemPayload: (input: {
    id: string;
    payload: TRenderPayload;
    profileSnapshotId?: string;
    profileRecencyToken?: string;
  }) => void;

  loadHistory: (input: {
    conversationId: string | null;
    messages: ChatMessage<TRenderPayload>[];
    dockItems: ChatDockItem<TRenderPayload>[];
  }) => void;
};

function nextId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function appendAssistantText(existing: string, chunk: string): string {
  if (!existing || !chunk) return existing + chunk;

  const lastChar = existing.at(-1) ?? "";
  const firstChar = chunk[0] ?? "";
  const boundaryNeedsSpace =
    /[.!?:)]/.test(lastChar) && /[A-Z0-9"'*]/.test(firstChar);

  return boundaryNeedsSpace ? `${existing} ${chunk}` : `${existing}${chunk}`;
}

export function createChatStore<TRenderPayload = unknown>(): UseBoundStore<
  StoreApi<ChatStore<TRenderPayload>>
> {
  return create<ChatStore<TRenderPayload>>((set) => ({
    messages: [],
    isStreaming: false,
    conversationId: null,
    activeToolCallIds: [],
    currentToolName: "",
    dockItems: [],
    lastError: null,
    hasUnreadAdvisorMessage: false,
    systemNotices: [],
    pendingFeedbackPrompt: null,
    needsAuthRefresh: false,
    profileSnapshotRefreshNonce: 0,
    latestProfileSnapshotId: null,
    latestProfileRecencyToken: null,

    addMessage: (message) =>
      set((state) => ({ messages: [...state.messages, message] })),

    appendToLast: (chunk) =>
      set((state) => {
        const messages = [...state.messages];
        const last = messages[messages.length - 1];
        if (!last || last.role !== "advisor") {
          return { messages };
        }
        messages[messages.length - 1] = {
          ...last,
          text: appendAssistantText(last.text ?? "", chunk),
        };
        return { messages };
      }),

    setStreaming: (isStreaming) => set({ isStreaming }),
    stopActiveTurn: () =>
      set({ isStreaming: false, activeToolCallIds: [], currentToolName: "" }),
    setConversationId: (conversationId) => set({ conversationId }),
    markAdvisorMessagesRead: () => set({ hasUnreadAdvisorMessage: false }),
    clearMessages: () =>
      set({
        messages: [],
        conversationId: null,
        activeToolCallIds: [],
        currentToolName: "",
        dockItems: [],
        lastError: null,
        hasUnreadAdvisorMessage: false,
        systemNotices: [],
        pendingFeedbackPrompt: null,
        needsAuthRefresh: false,
        profileSnapshotRefreshNonce: 0,
        latestProfileSnapshotId: null,
        latestProfileRecencyToken: null,
      }),

    dismissSystemNotice: (id) =>
      set((state) => ({
        systemNotices: state.systemNotices.filter((n) => n.id !== id),
      })),

    clearPendingFeedbackPrompt: () => set({ pendingFeedbackPrompt: null }),

    clearAuthRefresh: () => set({ needsAuthRefresh: false }),

    updateDockItemPayload: ({
      id,
      payload,
      profileSnapshotId,
      profileRecencyToken,
    }) =>
      set((state) => {
        const existing = state.dockItems.find((item) => item.id === id);
        if (!existing) return state;

        const dockItems = state.dockItems.map((item) =>
          item.id === id
            ? {
                ...item,
                payload,
                profileSnapshotId: profileSnapshotId ?? item.profileSnapshotId,
                profileRecencyToken:
                  profileRecencyToken ?? item.profileRecencyToken,
                updatedAt: new Date().toISOString(),
              }
            : item,
        );

        const messages = state.messages.map((message) => {
          if (message.id !== existing.sourceMessageId) return message;

          const renders = message.renders ? [...message.renders] : undefined;
          const renderIndex = existing.renderIndex;
          if (
            renders &&
            renderIndex !== undefined &&
            renderIndex >= 0 &&
            renderIndex < renders.length
          ) {
            renders[renderIndex] = payload;
          } else if (renders) {
            const fallbackIndex = renders.findIndex(
              (candidate) => candidate === existing.payload,
            );
            if (fallbackIndex >= 0) renders[fallbackIndex] = payload;
          }

          const isLatestRender =
            renderIndex === undefined ||
            renderIndex === (message.renders?.length ?? 1) - 1;

          return {
            ...message,
            render: isLatestRender ? payload : message.render,
            renders,
            profileSnapshotId: profileSnapshotId ?? message.profileSnapshotId,
            profileRecencyToken:
              profileRecencyToken ?? message.profileRecencyToken,
          };
        });

        return { dockItems, messages };
      }),

    loadHistory: ({ conversationId, messages, dockItems }) =>
      set({
        messages,
        dockItems,
        conversationId,
        activeToolCallIds: [],
        lastError: null,
        systemNotices: [],
        pendingFeedbackPrompt: null,
        needsAuthRefresh: false,
        profileSnapshotRefreshNonce: 0,
        latestProfileSnapshotId: null,
        latestProfileRecencyToken:
          [...messages]
            .reverse()
            .find(
              (message) =>
                message.role === "advisor" && message.profileRecencyToken,
            )?.profileRecencyToken ?? null,
        isStreaming: false,
        hasUnreadAdvisorMessage: false,
      }),

    handleServerFrame: (frame) =>
      set((state) => {
        const f = frame as AnyChatServerFrame & Record<string, unknown>;
        switch (f.type) {
          case "text_delta": {
            const text = f.text as string;
            const messages = [...state.messages];
            const last = messages[messages.length - 1];
            if (last && last.role === "advisor") {
              messages[messages.length - 1] = {
                ...last,
                text: appendAssistantText(last.text ?? "", text),
              };
            } else {
              messages.push({
                id: nextId("advisor"),
                role: "advisor",
                text,
                createdAt: new Date().toISOString(),
              } as ChatMessage<TRenderPayload>);
            }
            return { messages, hasUnreadAdvisorMessage: true };
          }
          case "render": {
            const render = f.render as TRenderPayload;
            const profileSnapshotId = f.profile_snapshot_id as
              | string
              | undefined;
            const profileRecencyToken = f.profile_recency_token as
              | string
              | undefined;
            const messages = [...state.messages];
            const lastMessage = messages[messages.length - 1];
            const updatedAt = new Date().toISOString();
            const sourceMessageId =
              lastMessage?.role === "advisor"
                ? lastMessage.id
                : nextId("advisor");
            const renderIndex =
              lastMessage?.role === "advisor"
                ? (lastMessage.renders?.length ?? 0)
                : 0;
            if (lastMessage?.role === "advisor") {
              messages[messages.length - 1] = {
                ...lastMessage,
                render,
                renders: [...(lastMessage.renders ?? []), render],
                profileSnapshotId:
                  profileSnapshotId ?? lastMessage.profileSnapshotId,
                profileRecencyToken:
                  profileRecencyToken ?? lastMessage.profileRecencyToken,
              };
            } else {
              const anchor: ChatMessage<TRenderPayload> = {
                id: sourceMessageId,
                role: "advisor",
                render,
                renders: [render],
                createdAt: updatedAt,
                profileSnapshotId,
                profileRecencyToken,
              };
              messages.push(anchor);
            }
            return {
              messages,
              dockItems: [
                {
                  id: nextId("dock"),
                  payload: render,
                  renderIndex,
                  sourceMessageId,
                  updatedAt,
                  profileSnapshotId,
                  profileRecencyToken,
                },
                ...state.dockItems,
              ],
              latestProfileRecencyToken:
                profileRecencyToken ?? state.latestProfileRecencyToken,
              hasUnreadAdvisorMessage: true,
            };
          }
          case "tool_use_start":
            return {
              activeToolCallIds: [
                ...state.activeToolCallIds,
                f.toolCallId as string,
              ],
              currentToolName: (f.tool as string | undefined) ?? "",
            };
          case "tool_use_end": {
            const activeToolCallIds = state.activeToolCallIds.filter(
              (id) => id !== (f.toolCallId as string),
            );
            return {
              activeToolCallIds,
              // Clear the working label once the last tool finishes; leave it
              // while others are still running.
              currentToolName:
                activeToolCallIds.length === 0 ? "" : state.currentToolName,
            };
          }
          case "error":
            return {
              lastError: {
                code: f.code as string | undefined,
                error: f.error as string,
              },
              isStreaming: false,
            };
          case "end_turn":
            return { isStreaming: false };
          case "warning":
            return {
              systemNotices: [
                ...state.systemNotices.slice(-19),
                {
                  id: nextId("notice"),
                  severity: "warn",
                  code: f.code as string | undefined,
                  message: f.message as string,
                },
              ],
            };
          case "info":
            if (f.code === "profile_snapshot_updated") {
              return {
                latestProfileSnapshotId:
                  (f.profile_snapshot_id as string | undefined) ?? null,
                latestProfileRecencyToken:
                  (f.profile_recency_token as string | undefined) ??
                  state.latestProfileRecencyToken,
                profileSnapshotRefreshNonce:
                  state.profileSnapshotRefreshNonce + 1,
              };
            }
            if (f.code === "turn_cancelled") {
              return { isStreaming: false, activeToolCallIds: [] };
            }
            if (f.code === "resources_dirty") {
              return state;
            }
            return {
              systemNotices: [
                ...state.systemNotices.slice(-19),
                {
                  id: nextId("notice"),
                  severity: "info",
                  code: f.code as string | undefined,
                  message: (f.message as string | undefined) ?? "",
                },
              ],
            };
          case "feedback_request":
            return { pendingFeedbackPrompt: f.message as string };
          case "auth_refresh":
            return { needsAuthRefresh: true };
          case "followups": {
            const messages = [...state.messages];
            const last = messages[messages.length - 1];
            if (!last || last.role !== "advisor") {
              return state;
            }
            messages[messages.length - 1] = {
              ...last,
              followups: f.prompts as string[],
            };
            return { messages };
          }
          // extraction and otp_required are anon-session-only frames (M6).
          // The chat store handles the signed-in session; these are no-ops here.
          case "extraction":
          case "otp_required":
          default:
            return state;
        }
      }),
  }));
}
